// PlansCanvas2D.tsx — center canvas for Plans mode
// Renders the currently selected plan drawing as 2D SVG
// Dome: circle with diameter label
// Gable/flat/barrel-vault: future rendering

import type { PlansData, RoofPlan, RoofElement, WallSection, FootingSection, DoorType, WindowType, DrawingSheet, Elevation, PanelData, RoofStructure } from '../types/panelvar';
import { computeFootprint, computeRoofOutline } from '../utils/buildingFootprint';
import { computeRoofStructure, formatRoofStructureSummary } from '../utils/roofStructure';
import FloorPlanCanvas from './FloorPlanCanvas';

interface Props {
  plans: PlansData | null;
  activeTab: string;
  elevations?: Elevation[];
  panels?: PanelData[];
}

function RoofElementSVG({ el }: { el: RoofElement }) {
  if (el.type === 'dome') {
    const r = (el.diameter ?? 0) / 2;
    if (r <= 0) return null;
    return (
      <g>
        <circle cx={el.x} cy={el.y} r={r} fill="none" stroke="#4a90d9" strokeWidth={2} />
        <line x1={el.x - r} y1={el.y} x2={el.x + r} y2={el.y} stroke="#4a90d9" strokeWidth={1} strokeDasharray="6 3" />
        <text x={el.x} y={el.y - r - 8} textAnchor="middle" fontSize={14} fill="#333">
          {el.name || 'Dome'}
        </text>
        <text x={el.x} y={el.y + 16} textAnchor="middle" fontSize={12} fill="#666">
          ⌀{Math.round(el.diameter! / 12)}&apos;-0&quot;
        </text>
      </g>
    );
  }
  if (el.type === 'gable') {
    const w = el.width ?? 0;
    const h = el.ridgeHeight ?? 0;
    if (w <= 0) return null;
    return (
      <g>
        <polygon
          points={`${el.x - w / 2},${el.y} ${el.x},${el.y - h} ${el.x + w / 2},${el.y}`}
          fill="none" stroke="#d97b4a" strokeWidth={2}
        />
        <text x={el.x} y={el.y - h - 8} textAnchor="middle" fontSize={14} fill="#333">
          {el.name || 'Gable'}
        </text>
      </g>
    );
  }
  if (el.type === 'barrel-vault') {
    const w = el.diameter ?? 0;
    const l = el.length ?? 0;
    if (w <= 0 || l <= 0) return null;
    const rot = el.rotation ?? 0;
    return (
      <g transform={`rotate(${rot}, ${el.x}, ${el.y})`}>
        <rect x={el.x - w / 2} y={el.y - l / 2} width={w} height={l} fill="none" stroke="#4ad97b" strokeWidth={2} rx={4} />
        <ellipse cx={el.x} cy={el.y - l / 2} rx={w / 2} ry={w / 6} fill="none" stroke="#4ad97b" strokeWidth={1} strokeDasharray="6 3" />
        <text x={el.x} y={el.y - l / 2 - 8} textAnchor="middle" fontSize={14} fill="#333">
          {el.name || 'Barrel Vault'}
        </text>
      </g>
    );
  }
  if (el.type === 'flat') {
    const w = el.width ?? 0;
    const l = el.length ?? w;
    if (w <= 0) return null;
    const x0 = el.x - w / 2;
    const y0 = el.y - l / 2;
    // Scale stroke & text relative to element size so they're visible at any zoom
    const span = Math.max(w, l);
    const sw = span / 400;           // stroke width
    const fs = span / 25;            // label font
    const fsDim = span / 35;         // dimension font
    const dashLen = span / 60;
    const wFt = Math.floor(w / 12);
    const wIn = Math.round(w % 12);
    const lFt = Math.floor(l / 12);
    const lIn = Math.round(l % 12);
    const wLabel = wIn ? `${wFt}'-${wIn}"` : `${wFt}'-0"`;
    const lLabel = lIn ? `${lFt}'-${lIn}"` : `${lFt}'-0"`;
    return (
      <g>
        {/* Solid outline */}
        <rect x={x0} y={y0} width={w} height={l}
              fill="#f0f0f0" fillOpacity={0.35}
              stroke="#555" strokeWidth={sw} />
        {/* Dashed interior pattern */}
        <rect x={x0} y={y0} width={w} height={l}
              fill="none" stroke="#999" strokeWidth={sw * 0.5}
              strokeDasharray={`${dashLen} ${dashLen * 0.6}`} />
        {/* Name label */}
        <text x={el.x} y={el.y - fs * 0.3} textAnchor="middle" fontSize={fs} fill="#333" fontWeight="bold">
          {el.name || 'Flat Roof'}
        </text>
        {/* Width dimension (bottom) */}
        <text x={el.x} y={y0 + l + fsDim * 1.8} textAnchor="middle" fontSize={fsDim} fill="#555">
          {wLabel}
        </text>
        {/* Length dimension (right side, rotated) */}
        <text x={x0 + w + fsDim * 1.5} y={el.y} textAnchor="middle" fontSize={fsDim} fill="#555"
              transform={`rotate(-90, ${x0 + w + fsDim * 1.5}, ${el.y})`}>
          {lLabel}
        </text>
      </g>
    );
  }
  return null;
}

function RoofPlanCanvas({ roofPlan, elevations, panels }: { roofPlan: RoofPlan; elevations?: Elevation[]; panels?: PanelData[] }) {
  // Compute auto-generated outline from footprint if elevation data available
  const footprint = elevations && panels && elevations.length > 0
    ? computeFootprint(elevations, panels)
    : null;
  const roofOutline = footprint ? computeRoofOutline(footprint) : (roofPlan.outline ?? []);
  const hasOutline = roofOutline.length >= 3;

  // Compute structural data from outline
  const structuralData: RoofStructure | undefined = hasOutline
    ? (roofPlan.structuralData ?? computeRoofStructure(roofOutline))
    : undefined;

  if (!hasOutline && !roofPlan.elements.length) {
    return (
      <div className="building-canvas-empty">
        <p>No roof outline or elements yet.</p>
        <p className="building-canvas-hint">Add elevations to auto-generate the roof outline, or add elements manually.</p>
      </div>
    );
  }

  // Compute bounding box — auto outline takes priority over manual elements
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  if (hasOutline) {
    for (const p of roofOutline) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    // Also include exterior footprint corners for context
    if (footprint) {
      for (const c of footprint.corners) {
        minX = Math.min(minX, c.x);
        minY = Math.min(minY, c.y);
        maxX = Math.max(maxX, c.x);
        maxY = Math.max(maxY, c.y);
      }
    }
  } else {
    // No outline — fit to manual elements
    for (const el of roofPlan.elements) {
      const r = Math.max(el.diameter ?? 0, el.width ?? 0, el.length ?? 0) / 2 + 50;
      minX = Math.min(minX, el.x - r);
      minY = Math.min(minY, el.y - r);
      maxX = Math.max(maxX, el.x + r);
      maxY = Math.max(maxY, el.y + r);
    }
  }

  const spanX = maxX - minX;
  const spanY = maxY - minY;
  const viewSpan = Math.max(spanX, spanY, 600);
  const pad = viewSpan * 0.15; // Extra padding for annotations
  const vbX = minX - pad;
  const vbY = minY - pad;
  const vbW = spanX + pad * 2;
  const vbH = spanY + pad * 2;

  // Stroke widths proportional to view
  const sw = viewSpan / 500;
  const thinSw = sw * 0.5;
  const fontSize = viewSpan / 40;
  const smallFont = viewSpan / 55;
  const tinyFont = viewSpan / 70;

  // Joist line generation — cap at ~40 visual lines for performance
  const joistLines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  if (hasOutline && structuralData) {
    const oMinX = Math.min(...roofOutline.map(p => p.x));
    const oMaxX = Math.max(...roofOutline.map(p => p.x));
    const oMinY = Math.min(...roofOutline.map(p => p.y));
    const oMaxY = Math.max(...roofOutline.map(p => p.y));
    const nominalSpacing = structuralData.framingSpacing;

    // Calculate the dimension along which joists are spaced
    const spacingDim = structuralData.spanDirection === 'short'
      ? (oMaxY - oMinY)  // joists spaced along Y
      : (oMaxX - oMinX); // joists spaced along X
    const maxLines = 40;
    const actualCount = Math.floor(spacingDim / nominalSpacing);
    // If too many, use a visual spacing that shows ~maxLines representative lines
    const visualSpacing = actualCount > maxLines
      ? spacingDim / maxLines
      : nominalSpacing;

    if (structuralData.spanDirection === 'short') {
      const startY = oMinY + visualSpacing;
      for (let y = startY; y < oMaxY; y += visualSpacing) {
        joistLines.push({ x1: oMinX, y1: y, x2: oMaxX, y2: y });
      }
    } else {
      const startX = oMinX + visualSpacing;
      for (let x = startX; x < oMaxX; x += visualSpacing) {
        joistLines.push({ x1: x, y1: oMinY, x2: x, y2: oMaxY });
      }
    }
  }

  // Format dimensions for annotation
  const fmtIn = (val: number) => {
    const ft = Math.floor(Math.abs(val) / 12);
    const inc = Math.round(Math.abs(val) % 12);
    return inc === 0 ? `${ft}'-0"` : `${ft}'-${inc}"`;
  };

  return (
    <svg className="building-canvas-svg" viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`} preserveAspectRatio="xMidYMid meet">
      {/* Grid */}
      <defs>
        <pattern id="plans-grid" width={120} height={120} patternUnits="userSpaceOnUse">
          <path d="M 120 0 L 0 0 0 120" fill="none" stroke="#e8e8e8" strokeWidth={thinSw} />
        </pattern>
      </defs>
      <rect x={vbX} y={vbY} width={vbW} height={vbH} fill="url(#plans-grid)" />

      {/* Exterior wall outline (dashed) — shows where walls are for reference */}
      {footprint && footprint.corners.length >= 3 && (
        <polygon
          points={footprint.corners.map(c => `${c.x},${c.y}`).join(' ')}
          fill="#e8e0d0"
          fillOpacity={0.25}
          stroke="#999"
          strokeWidth={sw}
          strokeDasharray={`${viewSpan / 80} ${viewSpan / 120}`}
        />
      )}

      {/* Roof outline (solid, filled) — inset from walls by panel thickness */}
      {hasOutline && (
        <polygon
          points={roofOutline.map(p => `${p.x},${p.y}`).join(' ')}
          fill="#f4f8fc"
          stroke="#1a5276"
          strokeWidth={sw * 1.5}
        />
      )}

      {/* Joist lines */}
      {joistLines.map((jl, i) => (
        <line key={`j-${i}`}
          x1={jl.x1} y1={jl.y1} x2={jl.x2} y2={jl.y2}
          stroke="#5b8fb9"
          strokeWidth={thinSw}
          strokeDasharray={`${viewSpan / 100} ${viewSpan / 200}`}
        />
      ))}

      {/* Ledger annotations along each roof outline edge */}
      {hasOutline && structuralData && roofOutline.map((p, i) => {
        const next = roofOutline[(i + 1) % roofOutline.length];
        const mx = (p.x + next.x) / 2;
        const my = (p.y + next.y) / 2;
        const edgeDx = next.x - p.x;
        const edgeDy = next.y - p.y;
        const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
        if (edgeLen < 12) return null;
        // Inward normal for label offset
        const nx = -edgeDy / edgeLen;
        const ny = edgeDx / edgeLen;
        const labelX = mx + nx * fontSize * 1.5;
        const labelY = my + ny * fontSize * 1.5;
        // Rotation to align text with edge
        const angleDeg = Math.atan2(edgeDy, edgeDx) * 180 / Math.PI;
        const textAngle = (angleDeg > 90 || angleDeg < -90) ? angleDeg + 180 : angleDeg;
        return (
          <g key={`ledger-${i}`}>
            {/* Ledger symbol — thick line along inner face */}
            <line x1={p.x} y1={p.y} x2={next.x} y2={next.y}
              stroke="#c0392b" strokeWidth={sw * 2} />
            {/* Anchor bolt markers at spacing intervals */}
            {Array.from({ length: Math.max(1, Math.floor(edgeLen / structuralData.ledger.anchorBoltSpacing)) }, (_, bi) => {
              const t = (bi + 0.5) / Math.max(1, Math.floor(edgeLen / structuralData.ledger.anchorBoltSpacing));
              const bx = p.x + edgeDx * t;
              const by = p.y + edgeDy * t;
              return <circle key={bi} cx={bx} cy={by} r={sw * 2.5} fill="#c0392b" stroke="none" />;
            })}
            {/* Label */}
            <text x={labelX} y={labelY}
              textAnchor="middle" fontSize={tinyFont} fill="#8b0000"
              transform={`rotate(${textAngle}, ${labelX}, ${labelY})`}>
              {structuralData.ledger.angleSize}
            </text>
          </g>
        );
      })}

      {/* Manual roof elements — only shown when no auto outline (otherwise superseded) */}
      {!hasOutline && roofPlan.elements.map((el) => (
        <RoofElementSVG key={el.id} el={el} />
      ))}

      {/* Structural summary notes — bottom-left legend */}
      {structuralData && (() => {
        const lines = formatRoofStructureSummary(structuralData);
        const legendX = vbX + pad * 0.3;
        const legendY = vbY + vbH - pad * 0.3 - lines.length * tinyFont * 1.4;
        const legendW = viewSpan * 0.5;
        const legendH = (lines.length + 1) * tinyFont * 1.4;
        return (
          <g>
            <rect x={legendX} y={legendY - tinyFont}
              width={legendW} height={legendH}
              fill="white" fillOpacity={0.9} stroke="#ccc" strokeWidth={thinSw} rx={sw * 2} />
            <text x={legendX + tinyFont * 0.5} y={legendY}
              fontSize={smallFont} fontWeight="bold" fill="#1a2332">
              Roof Structure (ACI 318 / SJI)
            </text>
            {lines.map((line, li) => (
              <text key={li}
                x={legendX + tinyFont * 0.5}
                y={legendY + (li + 1) * tinyFont * 1.4}
                fontSize={tinyFont} fill="#555">
                {line}
              </text>
            ))}
          </g>
        );
      })()}

      {/* Title */}
      <text x={(minX + maxX) / 2} y={vbY + pad * 0.5}
        textAnchor="middle" fontSize={fontSize} fontWeight="bold" fill="#1a2332">
        {roofPlan.name}
      </text>

      {/* Span dimension annotation */}
      {hasOutline && structuralData && (() => {
        const oMinX = Math.min(...roofOutline.map(p => p.x));
        const oMaxX = Math.max(...roofOutline.map(p => p.x));
        const oMinY = Math.min(...roofOutline.map(p => p.y));
        const oMaxY = Math.max(...roofOutline.map(p => p.y));
        const dimY = oMaxY + fontSize * 2;
        const dimX = oMaxX + fontSize * 2;
        return (
          <g>
            {/* Horizontal dimension */}
            <line x1={oMinX} y1={dimY} x2={oMaxX} y2={dimY} stroke="#333" strokeWidth={thinSw} />
            <line x1={oMinX} y1={dimY - fontSize * 0.3} x2={oMinX} y2={dimY + fontSize * 0.3} stroke="#333" strokeWidth={thinSw} />
            <line x1={oMaxX} y1={dimY - fontSize * 0.3} x2={oMaxX} y2={dimY + fontSize * 0.3} stroke="#333" strokeWidth={thinSw} />
            <text x={(oMinX + oMaxX) / 2} y={dimY + smallFont * 1.2}
              textAnchor="middle" fontSize={smallFont} fill="#3b82f6">
              {fmtIn(oMaxX - oMinX)}
            </text>
            {/* Vertical dimension */}
            <line x1={dimX} y1={oMinY} x2={dimX} y2={oMaxY} stroke="#333" strokeWidth={thinSw} />
            <line x1={dimX - fontSize * 0.3} y1={oMinY} x2={dimX + fontSize * 0.3} y2={oMinY} stroke="#333" strokeWidth={thinSw} />
            <line x1={dimX - fontSize * 0.3} y1={oMaxY} x2={dimX + fontSize * 0.3} y2={oMaxY} stroke="#333" strokeWidth={thinSw} />
            <text x={dimX + smallFont * 1.2} y={(oMinY + oMaxY) / 2}
              textAnchor="middle" fontSize={smallFont} fill="#3b82f6"
              transform={`rotate(-90, ${dimX + smallFont * 1.2}, ${(oMinY + oMaxY) / 2})`}>
              {fmtIn(oMaxY - oMinY)}
            </text>
          </g>
        );
      })()}
    </svg>
  );
}

// ── Drawing sheet canvas (floor plans, foundation) ─────────────────────────

function DrawingSheetCanvas({ sheet, type }: { sheet: DrawingSheet; type: string }) {
  const w = 800, h = 500;
  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="sheet-grid" width={40} height={40} patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e8e8e8" strokeWidth={0.5} />
        </pattern>
      </defs>
      <rect width={w} height={h} fill="url(#sheet-grid)" />
      {/* Title block */}
      <rect x={20} y={20} width={w - 40} height={h - 40} fill="none" stroke="#aaa" strokeWidth={2} />
      <rect x={w - 260} y={h - 70} width={240} height={50} fill="#f8f8f8" stroke="#aaa" strokeWidth={1} />
      <text x={w - 140} y={h - 45} textAnchor="middle" fontSize={14} fontWeight="bold" fill="#1a2332">{sheet.name}</text>
      <text x={w - 140} y={h - 28} textAnchor="middle" fontSize={11} fill="#888">Scale: 1/{sheet.scale} | {type}</text>
      {/* Center label */}
      <text x={w / 2} y={h / 2 - 20} textAnchor="middle" fontSize={24} fill="#1a2332" fontWeight="bold">{sheet.name}</text>
      {sheet.notes && (
        <text x={w / 2} y={h / 2 + 10} textAnchor="middle" fontSize={13} fill="#888">{sheet.notes}</text>
      )}
    </svg>
  );
}

// ── Wall section canvas ────────────────────────────────────────────────────

function WallSectionCanvas({ wall }: { wall: WallSection }) {
  const w = 600, h = 500;
  // Stylized wall cross-section
  const wallThk = 80, wallHt = 280, baseY = 380;
  const cx = w / 2;
  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="wall-hatch" width={8} height={8} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1={0} y1={0} x2={0} y2={8} stroke="#ccc" strokeWidth={1} />
        </pattern>
      </defs>
      <rect width={w} height={h} fill="#fafafa" />
      {/* Ground line */}
      <line x1={40} y1={baseY} x2={w - 40} y2={baseY} stroke="#666" strokeWidth={2} />
      {/* Wall section */}
      <rect x={cx - wallThk / 2} y={baseY - wallHt} width={wallThk} height={wallHt} fill="url(#wall-hatch)" stroke="#333" strokeWidth={2} />
      {/* Dimension lines */}
      <line x1={cx - wallThk / 2 - 30} y1={baseY} x2={cx - wallThk / 2 - 30} y2={baseY - wallHt} stroke="#3b82f6" strokeWidth={1} />
      <line x1={cx - wallThk / 2 - 35} y1={baseY} x2={cx - wallThk / 2 - 25} y2={baseY} stroke="#3b82f6" strokeWidth={1} />
      <line x1={cx - wallThk / 2 - 35} y1={baseY - wallHt} x2={cx - wallThk / 2 - 25} y2={baseY - wallHt} stroke="#3b82f6" strokeWidth={1} />
      <text x={cx - wallThk / 2 - 40} y={baseY - wallHt / 2} textAnchor="end" fontSize={12} fill="#3b82f6" dominantBaseline="middle">Height</text>
      {/* Thickness dimension */}
      <line x1={cx - wallThk / 2} y1={baseY - wallHt - 20} x2={cx + wallThk / 2} y2={baseY - wallHt - 20} stroke="#3b82f6" strokeWidth={1} />
      <text x={cx} y={baseY - wallHt - 28} textAnchor="middle" fontSize={12} fill="#3b82f6">Thickness</text>
      {/* Labels */}
      <text x={cx} y={50} textAnchor="middle" fontSize={20} fontWeight="bold" fill="#1a2332">Section {wall.label}</text>
      {wall.notes && (
        <text x={cx} y={78} textAnchor="middle" fontSize={13} fill="#888">{wall.notes}</text>
      )}
      <text x={cx} y={baseY + 30} textAnchor="middle" fontSize={12} fill="#aaa">GRADE</text>
    </svg>
  );
}

// ── Footing section canvas ─────────────────────────────────────────────────

function FootingSectionCanvas({ footing }: { footing: FootingSection }) {
  const w = 600, h = 400;
  const cx = w / 2, baseY = 280;
  const ftgW = 180, ftgH = 50, stemW = 80, stemH = 120;
  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <rect width={w} height={h} fill="#fafafa" />
      {/* Ground */}
      <line x1={40} y1={baseY - stemH} x2={w - 40} y2={baseY - stemH} stroke="#666" strokeWidth={1} strokeDasharray="8 4" />
      <text x={w - 45} y={baseY - stemH - 6} textAnchor="end" fontSize={11} fill="#aaa">GRADE</text>
      {/* Footing pad */}
      <rect x={cx - ftgW / 2} y={baseY} width={ftgW} height={ftgH} fill="#e0e0e0" stroke="#333" strokeWidth={2} />
      {/* Stem wall */}
      <rect x={cx - stemW / 2} y={baseY - stemH} width={stemW} height={stemH} fill="#d0d0d0" stroke="#333" strokeWidth={2} />
      {/* Rebar dots */}
      {[-ftgW / 3, 0, ftgW / 3].map((dx, i) => (
        <circle key={i} cx={cx + dx} cy={baseY + ftgH / 2} r={5} fill="none" stroke="#e74c3c" strokeWidth={2} />
      ))}
      {/* Labels */}
      <text x={cx} y={40} textAnchor="middle" fontSize={20} fontWeight="bold" fill="#1a2332">Footing {footing.label}</text>
      {footing.notes && (
        <text x={cx} y={68} textAnchor="middle" fontSize={13} fill="#888">{footing.notes}</text>
      )}
      {/* Dimension */}
      <line x1={cx - ftgW / 2} y1={baseY + ftgH + 20} x2={cx + ftgW / 2} y2={baseY + ftgH + 20} stroke="#3b82f6" strokeWidth={1} />
      <line x1={cx - ftgW / 2} y1={baseY + ftgH + 15} x2={cx - ftgW / 2} y2={baseY + ftgH + 25} stroke="#3b82f6" strokeWidth={1} />
      <line x1={cx + ftgW / 2} y1={baseY + ftgH + 15} x2={cx + ftgW / 2} y2={baseY + ftgH + 25} stroke="#3b82f6" strokeWidth={1} />
      <text x={cx} y={baseY + ftgH + 38} textAnchor="middle" fontSize={12} fill="#3b82f6">Width</text>
    </svg>
  );
}

// ── Door type canvas ───────────────────────────────────────────────────────

function DoorTypeCanvas({ door }: { door: DoorType }) {
  const w = 600, h = 400;
  const cx = w / 2, baseY = 340;
  const dW = Math.min(door.width * 0.8, 200);
  const dH = Math.min(door.height * 0.8, 260);
  const wFt = `${Math.floor(door.width / 12)}'`;
  const hFt = `${Math.floor(door.height / 12)}'`;
  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <rect width={w} height={h} fill="#fafafa" />
      {/* Door outline */}
      <rect x={cx - dW / 2} y={baseY - dH} width={dW} height={dH} fill="#f0ead6" stroke="#333" strokeWidth={2} />
      {/* Door panel lines */}
      <line x1={cx} y1={baseY - dH + 8} x2={cx} y2={baseY - 8} stroke="#bbb" strokeWidth={1} />
      {/* Handle */}
      <circle cx={cx + dW / 2 - 16} cy={baseY - dH / 2} r={5} fill="none" stroke="#666" strokeWidth={2} />
      {/* Swing arc */}
      <path d={`M ${cx + dW / 2} ${baseY} A ${dW} ${dW} 0 0 0 ${cx + dW / 2 + dW * 0.7} ${baseY - dW * 0.7}`}
        fill="none" stroke="#3b82f6" strokeWidth={1} strokeDasharray="4 3" />
      {/* Dimensions */}
      <text x={cx} y={baseY + 20} textAnchor="middle" fontSize={14} fill="#3b82f6" fontWeight="600">{wFt} × {hFt}</text>
      {/* Label */}
      <text x={cx} y={40} textAnchor="middle" fontSize={20} fontWeight="bold" fill="#1a2332">{door.label}</text>
      {door.notes && (
        <text x={cx} y={68} textAnchor="middle" fontSize={13} fill="#888">{door.notes}</text>
      )}
    </svg>
  );
}

// ── Window type canvas ─────────────────────────────────────────────────────

function WindowTypeCanvas({ win }: { win: WindowType }) {
  const svgW = 600, svgH = 400;
  const cx = svgW / 2, cy = svgH / 2;
  const wW = Math.min(win.width * 0.8, 220);
  const wH = Math.min(win.height * 0.8, 220);
  const wFt = `${Math.floor(win.width / 12)}'`;
  const hFt = `${Math.floor(win.height / 12)}'`;
  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${svgW} ${svgH}`} preserveAspectRatio="xMidYMid meet">
      <rect width={svgW} height={svgH} fill="#fafafa" />
      {/* Window frame */}
      <rect x={cx - wW / 2} y={cy - wH / 2} width={wW} height={wH} fill="#d4e8f7" stroke="#333" strokeWidth={2} />
      {/* Mullions */}
      <line x1={cx} y1={cy - wH / 2} x2={cx} y2={cy + wH / 2} stroke="#555" strokeWidth={1.5} />
      <line x1={cx - wW / 2} y1={cy} x2={cx + wW / 2} y2={cy} stroke="#555" strokeWidth={1.5} />
      {/* Glass panes × symbol */}
      {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => {
        const px = cx + sx * wW / 4;
        const py = cy + sy * wH / 4;
        const d = Math.min(wW, wH) / 6;
        return (
          <g key={i}>
            <line x1={px - d} y1={py - d} x2={px + d} y2={py + d} stroke="#aac8e0" strokeWidth={1} />
            <line x1={px + d} y1={py - d} x2={px - d} y2={py + d} stroke="#aac8e0" strokeWidth={1} />
          </g>
        );
      })}
      {/* Dimensions */}
      <text x={cx} y={cy + wH / 2 + 24} textAnchor="middle" fontSize={14} fill="#3b82f6" fontWeight="600">{wFt} × {hFt}</text>
      {/* Label */}
      <text x={cx} y={40} textAnchor="middle" fontSize={20} fontWeight="bold" fill="#1a2332">{win.label}</text>
      {win.notes && (
        <text x={cx} y={68} textAnchor="middle" fontSize={13} fill="#888">{win.notes}</text>
      )}
    </svg>
  );
}

// ── Overall Building Plan — composite footprint ────────────────────────────

function OverallBuildingCanvas({ plans, elevations, panels: allPanels }: { plans: PlansData; elevations?: Elevation[]; panels?: PanelData[] }) {
  const w = 1000, h = 700;
  const cx = w / 2, cy = h / 2;

  // Use real footprint if elevation data is available, otherwise fall back to hardcoded rect
  const footprint = elevations && allPanels && elevations.length > 0
    ? computeFootprint(elevations, allPanels)
    : null;

  // Compute building outline polygon or fallback rect
  let outlinePoints: string | null = null;
  let bx0: number, by0: number, bw: number, bh: number;
  if (footprint && footprint.walls.length > 0) {
    // Bounding box of footprint
    let fMinX = Infinity, fMinY = Infinity, fMaxX = -Infinity, fMaxY = -Infinity;
    for (const wall of footprint.walls) {
      fMinX = Math.min(fMinX, wall.startX, wall.endX);
      fMinY = Math.min(fMinY, wall.startY, wall.endY);
      fMaxX = Math.max(fMaxX, wall.startX, wall.endX);
      fMaxY = Math.max(fMaxY, wall.startY, wall.endY);
    }
    const fW = fMaxX - fMinX || 1;
    const fH = fMaxY - fMinY || 1;
    // Scale to fit within 700×400 centered on canvas
    const maxW = 700, maxH = 400;
    const scale = Math.min(maxW / fW, maxH / fH);
    const offX = cx - (fMinX + fW / 2) * scale;
    const offY = cy - (fMinY + fH / 2) * scale;
    outlinePoints = footprint.walls
      .map(wall => `${wall.startX * scale + offX},${wall.startY * scale + offY}`)
      .join(' ');
    bx0 = fMinX * scale + offX;
    by0 = fMinY * scale + offY;
    bw = fW * scale;
    bh = fH * scale;
  } else {
    // Fallback hardcoded
    bw = 600; bh = 300;
    bx0 = cx - bw / 2; by0 = cy - bh / 2;
  }

  const roofPlan = plans.roofPlans[0];
  const wallCount = plans.wallSections.length;
  const floorCount = plans.floorPlans.length;
  const doorCount = plans.doorTypes.length;
  const windowCount = plans.windowTypes.length;
  const footingCount = plans.footingSections.length;

  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="overall-grid" width={40} height={40} patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e8e8e8" strokeWidth={0.5} />
        </pattern>
      </defs>
      <rect width={w} height={h} fill="url(#overall-grid)" />

      {/* Building footprint */}
      {outlinePoints ? (
        <polygon points={outlinePoints} fill="#f0f4f8" stroke="#1a2332" strokeWidth={2.5} />
      ) : (
        <rect x={bx0} y={by0} width={bw} height={bh} fill="#f0f4f8" stroke="#1a2332" strokeWidth={2.5} />
      )}

      {/* Nave centre line */}
      <line x1={bx0} y1={cy} x2={bx0 + bw} y2={cy} stroke="#3b82f6" strokeWidth={1} strokeDasharray="8 4" />
      <text x={bx0 + bw + 8} y={cy + 4} fontSize={11} fill="#3b82f6">Nave CL</text>

      {/* Transept centre line */}
      <line x1={cx} y1={by0} x2={cx} y2={by0 + bh} stroke="#3b82f6" strokeWidth={1} strokeDasharray="8 4" />
      <text x={cx + 4} y={by0 - 6} fontSize={11} fill="#3b82f6">Transept CL</text>

      {/* Overlay roof elements as simple outlines */}
      {roofPlan && roofPlan.elements.map(el => {
        // Map element coordinates to the canvas footprint
        // Elements use absolute coords; scale to fit in the footprint
        const allX = roofPlan.elements.map(e => e.x);
        const allY = roofPlan.elements.map(e => e.y);
        const maxR = roofPlan.elements.reduce((mx, e) => Math.max(mx, (e.diameter ?? e.width ?? e.length ?? 200) / 2), 100);
        const dataMinX = Math.min(...allX) - maxR, dataMaxX = Math.max(...allX) + maxR;
        const dataMinY = Math.min(...allY) - maxR, dataMaxY = Math.max(...allY) + maxR;
        const dataW = dataMaxX - dataMinX || 1, dataH = dataMaxY - dataMinY || 1;
        const scaleX = (bw - 40) / dataW, scaleY = (bh - 40) / dataH;
        const scale = Math.min(scaleX, scaleY);
        const elCX = bx0 + 20 + (el.x - dataMinX) * scale;
        const elCY = by0 + 20 + (el.y - dataMinY) * scale;

        if (el.type === 'dome') {
          const r = ((el.diameter ?? 0) / 2) * scale;
          return <circle key={el.id} cx={elCX} cy={elCY} r={r} fill="rgba(123,160,196,0.15)" stroke="#7ba0c4" strokeWidth={1.5} />;
        }
        if (el.type === 'barrel-vault') {
          const elW = ((el.diameter ?? 0)) * scale;
          const elL = ((el.length ?? 0)) * scale;
          const rot = el.rotation ?? 0;
          return (
            <g key={el.id} transform={`rotate(${rot}, ${elCX}, ${elCY})`}>
              <rect x={elCX - elW / 2} y={elCY - elL / 2} width={elW} height={elL} fill="rgba(106,191,138,0.15)" stroke="#6abf8a" strokeWidth={1.5} rx={3} />
            </g>
          );
        }
        if (el.type === 'gable') {
          const elW = ((el.width ?? 0)) * scale;
          const elH2 = ((el.ridgeHeight ?? 0)) * scale;
          return (
            <polygon key={el.id}
              points={`${elCX - elW / 2},${elCY} ${elCX},${elCY - elH2} ${elCX + elW / 2},${elCY}`}
              fill="rgba(217,160,74,0.15)" stroke="#d9a04a" strokeWidth={1.5}
            />
          );
        }
        return null;
      })}

      {/* Summary legend */}
      <rect x={20} y={h - 120} width={200} height={100} fill="white" stroke="#ccc" strokeWidth={1} rx={4} />
      <text x={30} y={h - 100} fontSize={13} fontWeight="bold" fill="#1a2332">Building Summary</text>
      <text x={30} y={h - 82} fontSize={12} fill="#555">{floorCount} Floor Plans</text>
      <text x={30} y={h - 66} fontSize={12} fill="#555">{roofPlan ? roofPlan.elements.length : 0} Roof Elements</text>
      <text x={30} y={h - 50} fontSize={12} fill="#555">{wallCount} Wall Sections · {footingCount} Footings</text>
      <text x={30} y={h - 34} fontSize={12} fill="#555">{doorCount} Door Types · {windowCount} Window Types</text>

      {/* Title */}
      <text x={cx} y={30} textAnchor="middle" fontSize={22} fontWeight="bold" fill="#1a2332">Overall Building Plan</text>

      {/* Dimension lines */}
      <line x1={bx0} y1={by0 + bh + 25} x2={bx0 + bw} y2={by0 + bh + 25} stroke="#333" strokeWidth={1} />
      <line x1={bx0} y1={by0 + bh + 20} x2={bx0} y2={by0 + bh + 30} stroke="#333" strokeWidth={1} />
      <line x1={bx0 + bw} y1={by0 + bh + 20} x2={bx0 + bw} y2={by0 + bh + 30} stroke="#333" strokeWidth={1} />

      <line x1={bx0 - 25} y1={by0} x2={bx0 - 25} y2={by0 + bh} stroke="#333" strokeWidth={1} />
      <line x1={bx0 - 30} y1={by0} x2={bx0 - 20} y2={by0} stroke="#333" strokeWidth={1} />
      <line x1={bx0 - 30} y1={by0 + bh} x2={bx0 - 20} y2={by0 + bh} stroke="#333" strokeWidth={1} />

      {/* Direction markers */}
      <text x={cx} y={by0 - 10} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#1a2332">BACK (B)</text>
      <text x={cx} y={by0 + bh + 45} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#1a2332">FRONT (F)</text>
      <text x={bx0 - 40} y={cy + 4} textAnchor="end" fontSize={13} fontWeight="bold" fill="#1a2332">LEFT (L)</text>
      <text x={bx0 + bw + 40} y={cy + 4} fontSize={13} fontWeight="bold" fill="#1a2332">RIGHT (R)</text>
    </svg>
  );
}

// ── Foundation Plan — footing layout ───────────────────────────────────────

function FoundationCanvas({ plans }: { plans: PlansData }) {
  const w = 900, h = 600;
  const cx = w / 2, cy = h / 2;
  const bw = 560, bh = 280;
  const x0 = cx - bw / 2, y0 = cy - bh / 2;

  const footings = plans.footingSections;

  return (
    <svg className="building-canvas-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="fdn-grid" width={40} height={40} patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e8e8e8" strokeWidth={0.5} />
        </pattern>
        <pattern id="fdn-hatch" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1={0} y1={0} x2={0} y2={6} stroke="#ddd" strokeWidth={0.5} />
        </pattern>
      </defs>
      <rect width={w} height={h} fill="url(#fdn-grid)" />

      {/* Building outline (dashed — above grade) */}
      <rect x={x0} y={y0} width={bw} height={bh} fill="none" stroke="#aaa" strokeWidth={1.5} strokeDasharray="10 5" />

      {/* Continuous footing around perimeter */}
      <rect x={x0 - 8} y={y0 - 8} width={bw + 16} height={bh + 16} fill="url(#fdn-hatch)" stroke="#666" strokeWidth={2} />
      <rect x={x0 + 8} y={y0 + 8} width={bw - 16} height={bh - 16} fill="#fafafa" stroke="#666" strokeWidth={1} />

      {/* Individual footing pads — evenly placed */}
      {footings.map((ft, i) => {
        const cols = Math.min(footings.length, 4);
        const row = Math.floor(i / cols);
        const col = i % cols;
        const padW = 50, padH = 50;
        const spacingX = (bw - 80) / Math.max(cols - 1, 1);
        const spacingY = (bh - 80) / Math.max(Math.ceil(footings.length / cols) - 1, 1);
        const px = x0 + 40 + col * spacingX - padW / 2;
        const py = y0 + 40 + row * spacingY - padH / 2;
        return (
          <g key={ft.id}>
            <rect x={px} y={py} width={padW} height={padH} fill="#e0e0e0" stroke="#555" strokeWidth={1.5} />
            <text x={px + padW / 2} y={py + padH / 2 + 4} textAnchor="middle" fontSize={11} fontWeight="bold" fill="#333">
              F{i + 1}
            </text>
          </g>
        );
      })}

      {/* Title */}
      <text x={cx} y={30} textAnchor="middle" fontSize={22} fontWeight="bold" fill="#1a2332">Foundation Plan</text>
      <text x={cx} y={h - 20} textAnchor="middle" fontSize={12} fill="#888">{footings.length} footing sections · Continuous perimeter footing</text>
    </svg>
  );
}

export default function PlansCanvas2D({ plans, activeTab, elevations, panels }: Props) {
  if (!plans) {
    return (
      <div className="building-canvas-empty">
        <p>No plans data yet.</p>
        <p className="building-canvas-hint">Select a category from the sidebar to begin.</p>
      </div>
    );
  }

  // Roof plans — full SVG drawing
  if (activeTab.startsWith('roof-')) {
    const roofId = activeTab.replace('roof-', '');
    const roofPlan = plans.roofPlans.find(r => r.id === roofId);
    if (roofPlan) return <RoofPlanCanvas roofPlan={roofPlan} elevations={elevations} panels={panels} />;
  }

  // Floor plan detail — use real footprint renderer when elevation data available
  if (activeTab.startsWith('floor-')) {
    const floorId = activeTab.replace('floor-', '');
    const floor = plans.floorPlans.find(f => f.id === floorId);
    if (floor && elevations && panels && elevations.length > 0) {
      return <FloorPlanCanvas elevations={elevations} panels={panels} sheetName={floor.name} />;
    }
    if (floor) return <DrawingSheetCanvas sheet={floor} type="Floor Plan" />;
  }

  // Wall section detail
  if (activeTab.startsWith('wall-')) {
    const wallId = activeTab.replace('wall-', '');
    const wall = plans.wallSections.find(w => w.id === wallId);
    if (wall) return <WallSectionCanvas wall={wall} />;
  }

  // Footing section detail
  if (activeTab.startsWith('footing-')) {
    const footId = activeTab.replace('footing-', '');
    const foot = plans.footingSections.find(f => f.id === footId);
    if (foot) return <FootingSectionCanvas footing={foot} />;
  }

  // Door type detail
  if (activeTab.startsWith('door-')) {
    const doorId = activeTab.replace('door-', '');
    const door = plans.doorTypes.find(d => d.id === doorId);
    if (door) return <DoorTypeCanvas door={door} />;
  }

  // Window type detail
  if (activeTab.startsWith('window-')) {
    const winId = activeTab.replace('window-', '');
    const win = plans.windowTypes.find(w => w.id === winId);
    if (win) return <WindowTypeCanvas win={win} />;
  }

  // Overall Building Plan — composite showing roof + elevations outline
  if (activeTab === 'overall') {
    return <OverallBuildingCanvas plans={plans} elevations={elevations} panels={panels} />;
  }

  // Foundation plan — show footing layout
  if (activeTab === 'foundation') {
    return <FoundationCanvas plans={plans} />;
  }

  // Top-level category tabs
  const tabLabels: Record<string, string> = {
    floors: 'Floor Plans',
    roofs: 'Roof Plans',
    walls: 'Wall Sections',
    footings: 'Footing Sections',
    doors: 'Door Types',
    windows: 'Window Types',
  };

  const label = tabLabels[activeTab] ?? activeTab;

  return (
    <div className="building-canvas-empty">
      <h3>{label}</h3>
      <p className="building-canvas-hint">Select an item from the sidebar to view its drawing.</p>
    </div>
  );
}
